import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CustomTable, CustomTableRef, ETypeOfNotification, setNotification } from 'medere-ui-framework';
import { Column } from 'primereact/column';
import { getFinancerPendingDetails } from '../../servicesHooks/Honorarium/useHonorariumLiquidationService';
import { parseApiError } from '../../services/apiError';
import { FinancerPendingDetailDTO, PaginatedFinancerPendingDetailsDTO } from '../../dto/FinancerDistributionDTO';
import { getDistributionContractTypeDescription } from '../../enums/EDistributionContractType';
import StateBadge from './StateBadge';
import MovementTypeTag from '../../components/HonorariumMovement/MovementTypeTag';
import SignedAmount from '../../components/HonorariumMovement/SignedAmount';
import { movementKey } from '../../utils/honorariumMovement';
import {
    AppliedSearch, BENEFIT_ORDER_STATES, FORM_STATE_TYPES, PAGE_SIZE, SelectOption,
    buildPendingQueryParams, formatDate, formatHonorariumPercentage, formatMoney, unknownStateBadge,
} from './financerDistributionFormat';

interface FinancerDetailTableProps {
    search: AppliedSearch;
    professionalId: number;
    paymentMethodOptions: SelectOption[];
}

type FinancerRow = FinancerPendingDetailDTO & { rowKey: string };

/** La clave de movimiento se puede repetir entre filas (una anulación sobre dos ítems): el índice la hace única. */
function buildRowKey(d: FinancerPendingDetailDTO, index: number): string {
    return `${movementKey(d)}#${index}`;
}

/** Movimientos pendientes de un profesional, paginados en el backend con el scroll y los filtros de la búsqueda aplicada. */
export default function FinancerDetailTable({ search, professionalId, paymentMethodOptions }: FinancerDetailTableProps) {
    const dispatch = useDispatch();
    const tableRef = useRef<CustomTableRef>(null);
    // Totales del servidor sobre todo el resultado: llegan con la primera página
    const [totalCount, setTotalCount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalProfessional, setTotalProfessional] = useState(0);

    const getRows = useCallback(async (offset: number, pageSize: number): Promise<FinancerRow[]> => {
        try {
            const data: PaginatedFinancerPendingDetailsDTO =
                await getFinancerPendingDetails(buildPendingQueryParams(search, professionalId, offset, pageSize));
            if (offset === 0) {
                setTotalCount(data.totalCount);
                setTotalAmount(data.totalAmount);
                setTotalProfessional(data.totalProfessionalAmount);
            }
            return (data.items ?? []).map((d, i) => ({ ...d, rowKey: buildRowKey(d, offset + i) }));
        } catch (error: unknown) {
            // CustomTable no captura el error: sin esto queda el spinner colgado
            parseApiError(error);
            dispatch(setNotification({
                title: 'No se pudo cargar el detalle. Intentá de nuevo.',
                icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return [];
        }
    }, [search, professionalId, dispatch]);

    // Al montarse carga sola; si cambian la búsqueda o el profesional, vuelve a la primera página
    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        tableRef.current?.refreshTable?.();
    }, [getRows]);

    return (
        <>
            <div className="flex justify-content-end" style={{ marginBottom: 'var(--space-3)' }}>
                <span style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
                    {totalCount} movimiento(s)
                    {' — Total: '}<strong>{formatMoney(totalAmount)}</strong>
                    {' — Profesional: '}<strong>{formatMoney(totalProfessional)}</strong>
                </span>
            </div>

            <CustomTable
                idTable="tableFinancerPendingDetail"
                ref={tableRef}
                getItems={getRows}
                pageSize={PAGE_SIZE}
                className="crudTable"
                filterRow={false}
                hideFiltersButton
                stateless
                notFlex
                maxHeight={560}
                notFoundText="No hay resultados para estos filtros."
            >
                <Column field="sourceRecordId" header="Orden #" style={{ width: '90px' }} />
                <Column header="Fecha" body={(d: FinancerRow) => formatDate(d.date)} style={{ width: '100px' }} />
                <Column header="Pagador" body={(d: FinancerRow) => d.payerTypeName ?? '—'} style={{ width: '110px' }} />
                <Column header="Paciente" body={(d: FinancerRow) => d.patientName ?? '—'} />
                <Column header="Obra Social" body={(d: FinancerRow) => d.healthEntityName ?? '—'} />
                <Column header="Práctica" body={(d: FinancerRow) => d.nomenclatorDescription ?? '—'} />
                <Column header="Movimiento" body={(d: FinancerRow) => <MovementTypeTag movementType={d.movementType} detail={d.movementDetail} />} />
                <Column header="Monto" body={(d: FinancerRow) => <SignedAmount amount={d.amount} format={formatMoney} />}
                    className="text-right" headerClassName="col-right" style={{ width: '120px' }} />
                <Column header="Tipo distribución" body={(d: FinancerRow) => getDistributionContractTypeDescription(d.distributionContractType)} style={{ width: '130px' }} />
                <Column header="% Hon." body={(d: FinancerRow) => formatHonorariumPercentage(d)}
                    className="text-center" headerClassName="col-center" style={{ width: '80px' }} />
                <Column header="Monto prof." body={(d: FinancerRow) => <SignedAmount amount={d.professionalAmount} format={formatMoney} strong />}
                    className="text-right" headerClassName="col-right" style={{ width: '130px' }} />
                <Column
                    header="Estado orden"
                    body={(d: FinancerRow) => d.benefitOrderState == null
                        ? <span style={{ color: 'var(--text-2)', fontSize: 12 }}>—</span>
                        : <StateBadge {...(BENEFIT_ORDER_STATES[d.benefitOrderState] ?? unknownStateBadge(d.benefitOrderState))} />}
                    className="text-center" headerClassName="col-center" style={{ width: '150px' }}
                />
                <Column
                    header="Estado factura orden"
                    body={(d: FinancerRow) => d.orderFormStateType == null
                        ? <span style={{ color: 'var(--text-2)', fontSize: 12 }}>Sin factura</span>
                        : <StateBadge {...(FORM_STATE_TYPES[d.orderFormStateType] ?? unknownStateBadge(d.orderFormStateType))} />}
                    className="text-center" headerClassName="col-center" style={{ width: '130px' }}
                />
                <Column
                    header="Medio de pago orden"
                    body={(d: FinancerRow) => {
                        if (d.orderPaymentMethod == null) return <span style={{ color: 'var(--text-2)', fontSize: 12 }}>—</span>;
                        const label = d.orderPaymentMethodName
                            ?? paymentMethodOptions.find(o => o.value === d.orderPaymentMethod)?.label
                            ?? `ID ${d.orderPaymentMethod}`;
                        return <span style={{ fontSize: 12 }}>{label}</span>;
                    }}
                    className="text-center" headerClassName="col-center" style={{ width: '150px' }}
                />
                <Column
                    header="Comprobante"
                    body={(d: FinancerRow) => {
                        if (!d.administrativeFormId) {
                            return <span style={{ color: 'var(--text-2)', fontSize: 12 }}>—</span>;
                        }
                        return (
                            <div className="flex flex-column" style={{ gap: '2px', fontSize: 12 }}>
                                <span>Nro {d.formNumber} — {formatDate(d.formDate)}</span>
                                {d.invoicePendingAmount != null && (
                                    <span style={{ color: 'var(--text-2)' }}>Pend: {formatMoney(d.invoicePendingAmount)}</span>
                                )}
                            </div>
                        );
                    }}
                    style={{ width: '200px' }}
                />
            </CustomTable>
        </>
    );
}
